import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Thermometer, Wind, Share2, Loader2, Droplets, Scale, RotateCcw, Angry, Frown, Meh, Smile, Laugh, Info } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { cn } from '../lib/utils';
import { formatWeight } from '../functions/formatWeight';
import { formatFluid } from '../functions/formatFluid';
import { formatTemp } from '../functions/formatTemp';
import { formatDateByPreference } from '../functions/formatDateByPreference';
import { getDateLocale } from '../functions/getDateLocale';
import { useLanguage } from '../functions/useLanguage';
import KodaCareLogo from './Custom Icons/KodaCareLogo';
import MucousMembraneIcon from './Custom Icons/MucousMembraneIcon';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface OverviewCardProps {
  title?: string;
  dateStr: string;
  isMetric: boolean;
  dateFormat: string;
  showInfoHint?: boolean;
  weight: number | null;
  fluidIn: number;
  foodIn: number;
  hrLog: { heart_rate_bpm: number } | null;
  rrLog: { resp_rate_bpm: number } | null;
  tempLog: { temp_c: number } | null;
  mucousMembrane: string | null;
  obsLog: { mood?: number; observations_text?: string } | null;
  onUndoObservations?: () => void;
  onShare?: () => void;
  showBranding?: boolean;
  sharing?: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function OverviewCard({
  title,
  dateStr,
  isMetric,
  dateFormat,
  showInfoHint = true,
  weight,
  fluidIn,
  foodIn,
  hrLog,
  rrLog,
  tempLog,
  mucousMembrane,
  obsLog,
  onUndoObservations,
  onShare,
  showBranding,
  sharing,
}: OverviewCardProps) {
  const { t, language } = useLanguage();
  const date = parseISO(dateStr);
  const dateLocale = getDateLocale(language);
  const [showHowToHint, setShowHowToHint] = useState(false);
  const [pumpFluid, setPumpFluid] = useState(false);
  const [pumpWeight, setPumpWeight] = useState(false);
  const [pumpHR, setPumpHR] = useState(false);
  const [pumpRR, setPumpRR] = useState(false);
  const [pumpMM, setPumpMM] = useState(false);
  const prevFluid = useRef(fluidIn);
  const prevWeight = useRef(weight);
  const prevHR = useRef(hrLog?.heart_rate_bpm);
  const prevRR = useRef(rrLog?.resp_rate_bpm);
  const prevMM = useRef(mucousMembrane);
  const pump = (set: (v: boolean) => void) => { set(true); setTimeout(() => set(false), 350); };
  useEffect(() => { if (fluidIn !== prevFluid.current) { prevFluid.current = fluidIn; pump(setPumpFluid); } }, [fluidIn]);
  useEffect(() => { if (weight !== prevWeight.current) { prevWeight.current = weight; pump(setPumpWeight); } }, [weight]);
  useEffect(() => { if (hrLog?.heart_rate_bpm !== prevHR.current) { prevHR.current = hrLog?.heart_rate_bpm; pump(setPumpHR); } }, [hrLog]);
  useEffect(() => { if (rrLog?.resp_rate_bpm !== prevRR.current) { prevRR.current = rrLog?.resp_rate_bpm; pump(setPumpRR); } }, [rrLog]);
  useEffect(() => { if (mucousMembrane !== prevMM.current) { prevMM.current = mucousMembrane; pump(setPumpMM); } }, [mucousMembrane]);

  const renderValueWithUnit = (formattedValue: string, pumpClass?: string) => {
    const match = formattedValue.match(/^([\d.,-]+)\s*(.*)$/);
    const primary = match?.[1] ?? formattedValue;
    const unit = match?.[2]?.trim() ?? '';
    return (
      <div className={cn('text-xl font-bold transition-transform duration-200', pumpClass)}>
        <span>{primary}</span>
        {unit ? <span className="ml-1 text-[0.5em] font-medium text-white/65">{unit}</span> : null}
      </div>
    );
  };
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  void renderValueWithUnit;

  return (
    <div className="bg-primary-brown p-6 text-white space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          {title ? (
            <>
              <div className="flex items-center gap-2">
                <p className="text-lg font-black leading-tight">{title}</p>
                {showInfoHint && (
                  <button
                    type="button"
                    onClick={() => setShowHowToHint(v => !v)}
                    className="p-1 rounded-full bg-white/15 hover:bg-white/25 text-white/90 transition-colors"
                    aria-label="Show how to vitals help"
                    aria-expanded={showHowToHint}
                  >
                    <Info size={13} className="shrink-0" />
                  </button>
                )}
              </div>
              {showInfoHint && showHowToHint && (
                <p className="mt-1 text-[11px] text-white/90">
                  Need help checking vitals?
                  {' '}
                  <Link to="/library/how-to" className="font-semibold underline underline-offset-2 hover:text-white">
                    Open How To
                  </Link>
                </p>
              )}
              <p className="mt-2 text-xs font-medium text-white/65">{format(date, 'EEEE', { locale: dateLocale }) + ', ' + formatDateByPreference(dateStr, dateFormat)}</p>
            </>
          ) : (
            <p className="text-sm font-medium text-white/65">{format(date, 'EEEE', { locale: dateLocale }) + ', ' + formatDateByPreference(dateStr, dateFormat)}</p>
          )}
        </div>
        {onShare && (
          <button data-no-capture onClick={onShare} className="p-1.5 hover:bg-white/20 rounded-full text-white/70 transition-colors" title={t('records.shareCard')}>
            {sharing ? <Loader2 size={16} className="animate-spin" /> : <Share2 size={16} />}
          </button>
        )}
      </div>

      {/* Row 1: HR | RR | Weight */}
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-white/10 rounded-xl px-2.5 py-2 flex flex-col gap-1.5">
          <div className="flex items-center gap-1 text-[10px] font-bold opacity-70 uppercase tracking-wide truncate">
            <Heart size={10} className="text-background-light shrink-0" />
            <span className="truncate">{t('records.heartRate')}</span>
          </div>
          <div className={cn('text-lg font-bold leading-none transition-transform duration-200', pumpHR && 'scale-125')}>
            {hrLog?.heart_rate_bpm ?? '--'}
            <span className="ml-0.5 text-[0.5em] font-medium text-white/60">bpm</span>
          </div>
        </div>

        <div className="bg-white/10 rounded-xl px-2.5 py-2 flex flex-col gap-1.5">
          <div className="flex items-center gap-1 text-[10px] font-bold opacity-70 uppercase tracking-wide truncate">
            <Wind size={10} className="text-background-light shrink-0" />
            <span className="truncate">{t('records.respRate')}</span>
          </div>
          <div className={cn('text-lg font-bold leading-none transition-transform duration-200', pumpRR && 'scale-125')}>
            {rrLog?.resp_rate_bpm ?? '--'}
            <span className="ml-0.5 text-[0.5em] font-medium text-white/60">bpm</span>
          </div>
        </div>

        <div className="bg-white/10 rounded-xl px-2.5 py-2 flex flex-col gap-1.5">
          <div className="flex items-center gap-1 text-[10px] font-bold opacity-70 uppercase tracking-wide truncate">
            <Scale size={10} className="text-background-light shrink-0" />
            <span className="truncate">{t('records.weight')}</span>
          </div>
          <div className={cn('text-lg font-bold leading-none transition-transform duration-200', pumpWeight && 'scale-125')}>
            {formatWeight(weight, isMetric)}
          </div>
        </div>
      </div>

      {/* Row 2: Mucous | Temp | Intake */}
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-white/10 rounded-xl px-2.5 py-2 flex flex-col gap-1.5">
          <div className="flex items-center gap-1 text-[10px] font-bold opacity-70 uppercase tracking-wide truncate">
            <MucousMembraneIcon width={9} height={9} className="text-background-light shrink-0" />
            <span className="truncate">{t('records.mucousMembranes')}</span>
          </div>
          <div className={cn('text-lg font-bold leading-none capitalize transition-transform duration-200', pumpMM && 'scale-125')}>
            {mucousMembrane ?? '--'}
          </div>
        </div>
        <div className="bg-white/10 rounded-xl px-2.5 py-2 flex flex-col gap-1.5">
          <div className="flex items-center gap-1 text-[10px] font-bold opacity-70 uppercase tracking-wide truncate">
            <Thermometer size={10} className="text-background-light shrink-0" />
            <span className="truncate">Temp.</span>
          </div>
          <div className="text-lg font-bold leading-none">
            {tempLog?.temp_c ? formatTemp(tempLog.temp_c, isMetric) : '--'}
          </div>
        </div>
        <div className="bg-white/10 rounded-xl px-2.5 py-2 flex flex-col gap-1.5">
          <div className="flex items-center gap-1 text-[10px] font-bold opacity-70 uppercase tracking-wide truncate">
            <Droplets size={10} className="text-background-light shrink-0" />
            <span className="truncate">Intake</span>
          </div>
          <div className={cn('text-lg font-bold leading-none transition-transform duration-200', pumpFluid && 'scale-125')}>
            {formatFluid(fluidIn, isMetric)}
          </div>
          {foodIn > 0 && (
            <div className="text-[10px] text-white/55 leading-none">
              {isMetric
                ? (foodIn >= 1000 ? `+${(foodIn / 1000).toFixed(foodIn % 1000 === 0 ? 0 : 1)}g` : `+${foodIn}mg`) + ' food'
                : `+${(foodIn / 28349.5).toFixed(2)}oz food`}
            </div>
          )}
        </div>
      </div>

      {/* Row 3: Observations */}
      {obsLog && (
        <div className="bg-white/10 rounded-xl p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold opacity-80">{t('records.observations')}</span>
            {onUndoObservations && (
              <button onClick={onUndoObservations} className="p-1 hover:bg-white/20 rounded-full text-white/60 transition-colors" title={t('records.undoObservation')}>
                <RotateCcw size={13} />
              </button>
            )}
          </div>
          <div className="flex items-start gap-2">
            {obsLog.mood !== undefined && <span className="shrink-0 text-background-light">{([<Angry key="angry" size={20}/>,<Frown key="frown" size={20}/>,<Meh key="meh" size={20}/>,<Smile key="smile" size={20}/>,<Laugh key="laugh" size={20}/>])[obsLog.mood as number]}</span>}
            <div className="min-w-0">{obsLog.observations_text && <p className="text-sm opacity-90">{obsLog.observations_text}</p>}</div>
          </div>
        </div>
      )}
      {showBranding && (
        <div className="pt-3 border-t border-white/20 flex items-center justify-between">
          <KodaCareLogo className="text-background-light" style={{ height: 14, width: 'auto' }} />
          <span className="text-[10px] opacity-50 tracking-wide">kodacare.pet</span>
        </div>
      )}
    </div>
  );
}
